'use client';

import { useRef, useEffect } from 'react';
import { Input } from './Input';
import { loadMapsScript } from './maps-loader';

// SPECS: __tests__/address-autocomplete-lifecycle.test.tsx

export interface ParsedAddress {
  address_street: string;
  address_city: string;
  address_state: string;
  address_zip: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (_street: string) => void;
  onSelect: (_address: ParsedAddress) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = '123 Main St',
  autoFocus,
  className,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const onSelectRef = useRef(onSelect);

  // Keep the callback ref up to date without assigning during render
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  // Every effect lifetime owns its async work, listener and polling timers.
  useEffect(() => {
    let disposed = false;
    let autocomplete: google.maps.places.Autocomplete | null = null;
    let listener: google.maps.MapsEventListener | null = null;
    let interval: ReturnType<typeof setInterval> | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const clearPolling = () => {
      clearInterval(interval);
      clearTimeout(timeout);
      interval = undefined;
      timeout = undefined;
    };

    const initAutocomplete = () => {
      if (disposed || !inputRef.current || autocomplete) return true;
      if (!window.google?.maps?.places) return false;

      const ac = new google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address'],
      });

      listener = ac.addListener('place_changed', () => {
        if (disposed) return;
        const place = ac.getPlace();
        if (!place.address_components) return;

        let streetNumber = '';
        let route = '';
        let city = '';
        let state = '';
        let zip = '';

        for (const comp of place.address_components) {
          const type = comp.types[0];
          if (type === 'street_number') streetNumber = comp.short_name;
          else if (type === 'route') route = comp.short_name;
          else if (type === 'locality') city = comp.long_name;
          else if (type === 'administrative_area_level_1') state = comp.short_name;
          else if (type === 'postal_code') zip = comp.short_name;
        }

        const fullStreet = [streetNumber, route].filter(Boolean).join(' ');

        onSelectRef.current({
          address_street: fullStreet,
          address_city: city,
          address_state: state || 'MO',
          address_zip: zip,
        });
      });

      autocomplete = ac;
      return true;
    };

    loadMapsScript().then(() => {
      if (initAutocomplete()) return;

      interval = setInterval(() => {
        if (initAutocomplete()) clearPolling();
      }, 500);
      timeout = setTimeout(clearPolling, 10000);
    });

    return () => {
      disposed = true;
      clearPolling();
      listener?.remove();
      autocomplete?.unbindAll();
      // Legacy Places exposes no supported instance-to-dropdown ownership API.
      // Leave SDK-owned DOM alone so another widget's dropdown stays usable.
    };
  }, []);

  return (
    <Input
      ref={inputRef}
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      className={className}
    />
  );
}
